import { useEffect, useCallback, useMemo } from "react";
import { useApolloClient, FetchResult } from "@apollo/client";
import { toast } from "@jsq/ds/core";
import { useGetDataVaultExtensionFieldsQuery } from "~/graphql/supergraphHooks";
import { type ExtensionFieldObjectType } from "~/graphql/supergraphTypes";
import {
  CREATE_DATA_VAULT_EXTENSION_FIELD,
  EDIT_DATA_VAULT_EXTENSION_FIELD,
  DELETE_DATA_VAULT_EXTENSION_FIELD,
  type CreateDataVaultExtensionFieldInput,
  type CreateDataVaultExtensionFieldMutation,
  type EditDataVaultExtensionFieldMutation,
  type DeleteDataVaultExtensionFieldMutation,
} from "../graphql";
import {
  CustomField,
  CustomFieldType,
  CustomFieldEntityType,
  CreateCustomFieldInput,
  EditDataVaultExtensionFieldInput,
  FIELD_TYPE_TO_BACKEND_ID,
  BACKEND_ID_TO_FIELD_TYPE,
} from "../types";

// Helper function to convert backend ExtensionFieldObjectType to frontend CustomField
const convertExtensionFieldToCustomField = (
  extensionField: {
    id?: string | number;
    name?: string;
    description?: string | null;
    extensionFieldTypeId?: number;
    enumName?: string | null;
    isMultivalued?: boolean | null;
    enumValues?: Array<{
      id?: string | number;
      enumValueLabel?: string;
      enumValueCode?: string;
    }> | null;
    activeEnumValues?: Array<{
      id?: string | number;
      enumValueLabel?: string;
      enumValueCode?: string;
    }> | null;
  } | null,
): CustomField => {
  // Handle null or empty objects from mocks - provide default values for testing
  const id = extensionField?.id || "mock-id";
  const name = extensionField?.name || "mock-field";
  const extensionFieldTypeId = extensionField?.extensionFieldTypeId || 3; // Default to TEXT

  const frontendType =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion -- the map is keyed by the enum, not by plain number
    BACKEND_ID_TO_FIELD_TYPE[extensionFieldTypeId as keyof typeof BACKEND_ID_TO_FIELD_TYPE] ||
    CustomFieldType.TEXT;

  return {
    id: String(id),
    name: name,
    label: name, // Backend doesn't have separate label, use name
    description: extensionField?.description || "", // Use actual description from GraphQL response
    type: frontendType,
    entityType: CustomFieldEntityType.ENTITY, // Default to ENTITY for Data Vault
    isActive: true, // Assume active if returned
    options:
      extensionField?.enumValues?.map((enumValue) => ({
        id: String(enumValue.id || ""),
        label: enumValue.enumValueLabel || "",
        value: enumValue.enumValueCode || "",
        isActive: true,
      })) || [],
    createdAt: new Date().toISOString(), // Backend doesn't provide timestamps
    updatedAt: new Date().toISOString(),
    createdBy: "System",
    updatedBy: "System",
  };
};

// Helper function to convert frontend CreateCustomFieldInput to backend CreateDataVaultExtensionFieldInput
const convertCreateInputToBackend = (
  input: CreateCustomFieldInput,
): CreateDataVaultExtensionFieldInput => {
  const backendTypeId = FIELD_TYPE_TO_BACKEND_ID[input.type];

  // Convert frontend options to backend enumValues format
  // Backend only needs enumValueLabel - it auto-generates enumValueCode
  const enumValues =
    input.type === CustomFieldType.ENUMERATION && input.options
      ? input.options.map((option) => ({
          enumValueLabel: option.label,
        }))
      : undefined;

  return {
    name: input.name,
    extensionFieldTypeId: backendTypeId,
    description: input.description,
    enumName: input.type === CustomFieldType.ENUMERATION ? input.name : null,
    enumValues: enumValues,
    isMultivalued: false, // For now, we don't support multi-valued fields in the UI
    isFilterable: true,
    isSearchable: false,
    sortOrder: null, // Should always be null. Let backend figure it out.
    sectionId: input.sectionId,
  };
};

// Interface for editField input
export interface EditCustomFieldInput {
  fieldId: string;
  name?: string;
  enumOptionsUpsert?: Array<{ id?: string; label: string }>;
  enumOptionsDelete?: Array<{ id: string }>;
  sectionId?: string; // Section to move the field to (undefined = no change, null = remove from section)
}

export interface UseCustomFieldsResult {
  data: CustomField[] | null;
  rawExtensionFields: ExtensionFieldObjectType[] | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
  createField: (input: CreateCustomFieldInput) => Promise<CustomField>;
  editField: (input: EditCustomFieldInput) => Promise<CustomField>;
  deleteField: (id: string) => Promise<boolean>;
}

export const useCustomFields = (): UseCustomFieldsResult => {
  const client = useApolloClient();

  // Use auto-generated query hook for better type safety
  const {
    data: queryData,
    loading,
    error: apolloError,
    refetch,
  } = useGetDataVaultExtensionFieldsQuery({
    variables: {
      input: { includeDeleted: false },
    },

    fetchPolicy: "cache-and-network", // Ensure fresh data on every load
    errorPolicy: "all", // Return partial data even if there are errors
    notifyOnNetworkStatusChange: true, // Get loading updates
  });

  // Store raw backend extension fields
  const rawExtensionFields = useMemo(() => {
    if (!queryData?.dataVaultExtensionFields) return null;

    return queryData.dataVaultExtensionFields.filter(
      (field): field is ExtensionFieldObjectType => field !== null,
    );
  }, [queryData]);

  // Transform backend data to frontend CustomField format
  const data = useMemo(() => {
    if (!queryData?.dataVaultExtensionFields) return null;

    return queryData.dataVaultExtensionFields
      .filter((field): field is NonNullable<typeof field> => field !== null)
      .map(convertExtensionFieldToCustomField);
  }, [queryData]);

  // Convert Apollo error to our error format with better error details
  const error = apolloError
    ? new Error(
        apolloError.message +
          (apolloError.graphQLErrors?.length > 0
            ? ` (GraphQL: ${apolloError.graphQLErrors.map((e) => e.message).join(", ")})`
            : "") +
          (apolloError.networkError ? ` (Network: ${apolloError.networkError.message})` : ""),
      )
    : null;

  // Show error toast when there's an error
  useEffect(() => {
    if (apolloError) {
      toast.error(`Error loading custom fields: ${apolloError.message}`);
    }
  }, [apolloError]);

  const createField = useCallback(
    async (input: CreateCustomFieldInput): Promise<CustomField> => {
      try {
        const backendInput = convertCreateInputToBackend(input);

        const result: FetchResult<CreateDataVaultExtensionFieldMutation> = await client.mutate({
          mutation: CREATE_DATA_VAULT_EXTENSION_FIELD,
          variables: { input: backendInput },

          refetchQueries: [
            "GetDataVaultExtensionFields",
            "GetDataVaultExtensionFieldSectionsByContext",
          ],
        });

        const mutationData = result.data;
        if (
          mutationData?.createDataVaultExtensionField?.errors?.length &&
          mutationData.createDataVaultExtensionField.errors.length > 0
        ) {
          throw new Error(mutationData.createDataVaultExtensionField.errors.join(", "));
        }

        if (!mutationData?.createDataVaultExtensionField?.extensionField) {
          throw new Error("No extension field returned from mutation");
        }

        const newField = convertExtensionFieldToCustomField(
          mutationData.createDataVaultExtensionField.extensionField,
        );

        return newField;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to create custom field";
        throw new Error(errorMessage);
      }
    },
    [client],
  );

  const editField = useCallback(
    async (input: EditCustomFieldInput): Promise<CustomField> => {
      try {
        // Convert frontend input to backend format
        const backendInput: EditDataVaultExtensionFieldInput = {
          fieldId: input.fieldId,
          name: input.name,
          enumOptionsUpsert: input.enumOptionsUpsert?.map((option) => ({
            id: option.id, // Optional - if present, update; if missing, create new
            enumValueLabel: option.label,
          })),
          enumOptionsDelete: input.enumOptionsDelete?.map((option) => ({
            id: option.id,
          })),
          sectionId: input.sectionId,
        };

        const result: FetchResult<EditDataVaultExtensionFieldMutation> = await client.mutate({
          mutation: EDIT_DATA_VAULT_EXTENSION_FIELD,
          variables: { input: backendInput },

          refetchQueries: [
            "GetDataVaultExtensionFields",
            "GetDataVaultExtensionFieldSectionsByContext",
          ],
        });

        const mutationData = result.data;
        if (
          mutationData?.editDataVaultExtensionField?.errors?.length &&
          mutationData.editDataVaultExtensionField.errors.length > 0
        ) {
          throw new Error(mutationData.editDataVaultExtensionField.errors.join(", "));
        }

        if (!mutationData?.editDataVaultExtensionField?.extensionField) {
          throw new Error("No extension field returned from edit mutation");
        }

        const updatedField = convertExtensionFieldToCustomField(
          mutationData.editDataVaultExtensionField.extensionField,
        );

        return updatedField;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to edit custom field";
        throw new Error(errorMessage);
      }
    },
    [client],
  );

  const deleteField = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result: FetchResult<DeleteDataVaultExtensionFieldMutation> = await client.mutate({
          mutation: DELETE_DATA_VAULT_EXTENSION_FIELD,
          variables: { input: { fieldId: id } },

          refetchQueries: [
            "GetDataVaultExtensionFields",
            "GetDataVaultExtensionFieldSectionsByContext",
          ],
        });

        const mutationData = result.data;
        if (
          mutationData?.deleteDataVaultExtensionField?.errors?.length &&
          mutationData.deleteDataVaultExtensionField.errors.length > 0
        ) {
          throw new Error(mutationData.deleteDataVaultExtensionField.errors.join(", "));
        }

        return mutationData?.deleteDataVaultExtensionField?.success || false;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete custom field";
        throw new Error(errorMessage);
      }
    },
    [client],
  );

  return {
    data,
    rawExtensionFields,
    loading,
    error,
    refetch,
    createField,
    editField,
    deleteField,
  };
};
